import type { ReactElement, RefObject } from "react";

import styles from "./Input.module.css";

export type InputType = "text" | "email" | "password" | "tel";

/** The soft keyboards this change asks for, and no more. */
export type InputMode = "text" | "numeric" | "decimal";

export interface InputProps {
  readonly id: string;
  /**
   * The controlled value. Leave it out for a secret (a password): React mirrors a controlled
   * value into the `value` ATTRIBUTE, where anything reading the markup would find it, so a
   * secret field stays uncontrolled and reports its value through `onChange` only.
   */
  readonly value?: string | undefined;
  readonly onChange: (value: string) => void;
  readonly type?: InputType | undefined;
  readonly autoComplete?: string | undefined;
  readonly invalid?: boolean | undefined;
  readonly describedBy?: string | undefined;
  readonly disabled?: boolean | undefined;
  /**
   * Shown and focusable but not editable, for a field whose value is being saved: unlike
   * `disabled`, it keeps the keyboard focus where the person is working (WCAG 2.4.3).
   */
  readonly readOnly?: boolean | undefined;
  /** Which soft keyboard a phone should offer for this field. */
  readonly inputMode?: InputMode | undefined;
  /**
   * A handle on the element, for the caller that has to move the focus.
   *
   * It exists because `autoFocus` is banned (`jsx-a11y/no-autofocus`): taking
   * the focus on page load is not the same thing as moving it to a field the
   * interface has just revealed, and only the second one is legitimate.
   */
  readonly inputRef?: RefObject<HTMLInputElement | null> | undefined;
  readonly placeholder?: string | undefined;
  /**
   * Enter runs this instead of submitting the form around the field (a field with its own action
   * inside a larger form, such as an image's alt text and its upload).
   */
  readonly onEnter?: (() => void) | undefined;
  /** Escape runs this and goes no further, so a popover around the field stays open (mockup v3 P4). */
  readonly onEscape?: (() => void) | undefined;
  /** The longest value the field accepts (the version note: 140). */
  readonly maxLength?: number | undefined;
  /** An accessible name for a field with no visible `<label>` (from `t()`). */
  readonly label?: string | undefined;
}

/**
 * A controlled text field whose accessibility comes from its props.
 *
 * `invalid` and `describedBy` are props rather than something the input
 * guesses, because the description lives in the `Field` around it and only
 * the caller knows whether the hint, the error, or both are on screen.
 * `onChange` hands over the value, never the event: a presentation component
 * has no business reading the DOM (F-UIX-009).
 */
export function Input({
  id,
  value,
  onChange,
  type = "text",
  autoComplete,
  invalid = false,
  describedBy,
  disabled = false,
  readOnly = false,
  inputMode,
  inputRef,
  placeholder,
  onEnter,
  onEscape,
  maxLength,
  label,
}: InputProps): ReactElement {
  return (
    <input
      id={id}
      ref={inputRef}
      className={styles.input}
      type={type}
      value={value}
      autoComplete={autoComplete}
      inputMode={inputMode}
      placeholder={placeholder}
      maxLength={maxLength}
      aria-label={label}
      aria-invalid={invalid}
      aria-describedby={describedBy}
      disabled={disabled}
      readOnly={readOnly}
      onChange={(event) => {
        onChange(event.target.value);
      }}
      onKeyDown={
        onEnter === undefined && onEscape === undefined
          ? undefined
          : (event) => {
              if (event.key === "Enter" && onEnter !== undefined) {
                event.preventDefault();
                onEnter();
              } else if (event.key === "Escape" && onEscape !== undefined) {
                event.preventDefault();
                event.stopPropagation();
                onEscape();
              }
            }
      }
    />
  );
}
